import type { CustomerConcentration } from "@payrollmart-invoice/shared";
import { formatDollars } from "@payrollmart-invoice/shared";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Users } from "lucide-react";

interface ConcentrationListProps {
	data: CustomerConcentration;
}

/**
 * "Where the money came from" card. Each row shows customer name, a horizontal
 * bar visualizing share of revenue, the %, and the dollar amount. The last
 * row is always the "Everyone else (M)" roll-up, rendered muted.
 */
export function ConcentrationList({ data }: ConcentrationListProps) {
	return (
		<div className="rounded-lg border border-[#e5e7eb] bg-white" data-testid="concentration-list">
			<div className="flex items-center justify-between border-b border-[#e5e7eb] px-6 py-4">
				<div className="flex items-center gap-2">
					<Users className="h-4 w-4 text-[#6b7280]" strokeWidth={1.5} />
					<h2 className="text-sm font-semibold text-[#1e1e20]">Where the money came from</h2>
				</div>
				<Link
					to="/customers"
					className="flex items-center gap-1 text-xs font-medium text-[#4f46e5] hover:underline"
				>
					All customers
					<ArrowRight className="h-3 w-3" strokeWidth={2} />
				</Link>
			</div>
			{data.rows.length === 0 ? (
				<div className="px-6 py-10 text-center text-sm text-[#6b7280]">
					No revenue recorded for {data.monthLabel} yet.
				</div>
			) : (
				<div>
					{data.rows.map((row, idx) => {
						// Bar fill width maxes out at the row with the largest %.
						// That keeps the dominant customer visually dominant rather
						// than showing every bar as a sliver of the 100% space.
						const max = Math.max(...data.rows.map((r) => r.pctOfTotal));
						const widthPct = max === 0 ? 0 : (row.pctOfTotal / max) * 100;
						const borderTop = idx === 0 ? "" : "border-t border-[#e5e7eb]";
						return (
							<div
								key={row.customerId ?? "other"}
								className={`grid grid-cols-[1fr_56px_80px] items-center gap-3.5 px-6 py-2.5 ${borderTop}`}
							>
								<div className="min-w-0">
									{row.isOther || !row.customerId ? (
										<div className="truncate text-[13px] font-medium text-[#6b7280]">
											{row.customerName}
										</div>
									) : (
										<Link
											to="/customers/$customerId"
											params={{ customerId: row.customerId }}
											className="truncate text-[13px] font-medium text-[#1e1e20] hover:text-[#4f46e5] hover:underline"
										>
											{row.customerName}
										</Link>
									)}
									<div className="relative mt-1.5 h-1 rounded-sm bg-[#f3f4f6]">
										<div
											className={`absolute inset-y-0 left-0 rounded-sm ${row.isOther ? "bg-[#d1d5db]" : "bg-[#4f46e5]"}`}
											style={{ width: `${widthPct}%` }}
										/>
									</div>
								</div>
								<div
									className={`text-right text-xs tabular-nums ${row.isOther ? "text-[#9ca3af]" : "text-[#1e1e20]"}`}
								>
									{row.pctOfTotal.toFixed(1)}%
								</div>
								<div
									className={`text-right text-xs tabular-nums ${row.isOther ? "text-[#9ca3af]" : "text-[#1e1e20]"}`}
								>
									{formatDollars(row.revenueCents)}
								</div>
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
}
